import { useCallback, useEffect, useState } from 'react';
import { applyTheme, getStoredTheme, getSystemTheme, type ThemeName } from '../lib/theme';

function currentDomTheme(): ThemeName {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.getAttribute('data-theme');
  return attr === 'dark' || attr === 'hc' ? (attr as ThemeName) : 'light';
}

/**
 * Read/toggle the active theme. `toggle`/`setTheme` persist the choice via
 * applyTheme. While the user has made no explicit choice, the hook keeps its
 * state in sync with OS colour-scheme changes so the toggle icon stays correct.
 */
export function useThemeMode() {
  const [theme, setThemeState] = useState<ThemeName>(currentDomTheme);

  useEffect(() => {
    setThemeState(currentDomTheme());
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => {
      if (getStoredTheme()) return;
      setThemeState(getSystemTheme());
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const setTheme = useCallback((next: ThemeName) => {
    applyTheme(next);
    setThemeState(next);
  }, []);

  const toggle = useCallback(() => {
    setTheme(currentDomTheme() === 'dark' ? 'light' : 'dark');
  }, [setTheme]);

  return { theme, isDark: theme === 'dark', setTheme, toggle };
}
